import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';
import dayjs from 'dayjs';
import { getClassroomVideocall, updateOrCreateVideocallInfo } from '../../../actions/classroom/classroomActions';
import { ROLE, GOOGLE_VIDEOCALL_SCOPES } from '../../../const';
import { CONTEXT_PATH } from '../../../config_module';
import offCall from '../../../images/offCall.svg';
import onCall from '../../../images/onCall.svg';
import { arePermissionsGranted, createGoogleMeet, requestPermissions } from '../../../utils/google/googleUtils';
import { v4 as uuid } from 'uuid';

let styles = {
  videocallIcon: {
    cursor: 'pointer',
    width: '53px',
    height: '37px',
    marginBottom: '4px',
    marginRight: '16px'
  },
  videocallIconOff: {
    width: '20px',
    height: '30px'
  },
  videocallIconElectron: {
    cursor: 'pointer',
    width: '53px',
    height: '37px',
    marginBottom: '4px',
    marginRight: '16px'
  },
  videocallIconOffelectron: {
    cursor: 'pointer',
    width: '20px',
    height: '30px',
    marginBottom: '4px',
    marginRight: '16px'
  },
  disabledButton: {
    opacity: 0.2
  }
};

class VideocallTopButton extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isOn: false,
      disabledButton: false,
      isHidden: false,
      videocallUrl: '',
      iconToShow: null,
      googleInit: false
    };

    this.createGoogleVideocall = this.createGoogleVideocall.bind(this);
  }

  async componentDidMount() {
    let isOn = false;
    const videocallInfo = await this.props.getClassroomVideocall(this.props.classroomId);

    if (videocallInfo) {
      isOn = !dayjs().isAfter(dayjs(videocallInfo.expiresAt).subtract(1, 'hours'));
    }

    this.setState({
      isOn,
      isHidden: false,
      videocallUrl: isOn ? videocallInfo.videocallUrl : '',
      iconToShow: isOn ? onCall : offCall
    });
  }

  static propTypes = {
    classroomId: PropTypes.string,
    classroomName: PropTypes.string,
    getClassroomVideocall: PropTypes.func,
    updateOrCreateVideocallInfo: PropTypes.func,
    role: PropTypes.string,
    isElectron: PropTypes.bool,
    googleMeetClientId: PropTypes.string,
    jitsiDomain: PropTypes.string
  };

  // Load calendar library and create an event from now, 2 hours long
  createGoogleVideocall = async () => {
    this.setState({ disabledButton: true });
    createGoogleMeet(this.props.classroomName).then(meetUrl => {
      this.setState(
        {
          isOn: true,
          isHidden: false,
          videocallUrl: meetUrl,
          iconToShow: onCall
        },
        async () => {
          await this.props.updateOrCreateVideocallInfo(this.props.classroomId, this.state.videocallUrl);
          this.setState({ disabledButton: false });
        }
      );
    });
  };

  createJitsiVideocall = async () => {
    this.setState({ disabledButton: true });
    const { jitsiDomain } = this.props;
    const meetUrl = `${jitsiDomain}/${this.props.classroomName.replace(' ', '-')}-${uuid()}`;

    this.setState(
      {
        isOn: true,
        isHidden: false,
        videocallUrl: meetUrl,
        iconToShow: onCall
      },
      async () => {
        await this.props.updateOrCreateVideocallInfo(this.props.classroomId, this.state.videocallUrl);
        this.setState({ disabledButton: false });
      }
    );
  };

  handleOnClick = async () => {
    const { googleMeetClientId, jitsiDomain } = this.props;
    if (googleMeetClientId) {
      const isGranted = await arePermissionsGranted(GOOGLE_VIDEOCALL_SCOPES);
      if (isGranted) {
        if (!this.state.isOn && this.props.role === ROLE.INSTRUCTOR) {
          this.createGoogleVideocall();
        } else {
          window.open(this.state.videocallUrl, '_blank');
        }
      } else {
        await requestPermissions(GOOGLE_VIDEOCALL_SCOPES, () => {
          if (!this.state.isOn && this.props.role === ROLE.INSTRUCTOR) {
            this.createGoogleVideocall();
          } else {
            window.open(this.state.videocallUrl, '_blank');
          }
        });
      }
    } else if (jitsiDomain) {
      if (!this.state.isOn && this.props.role === ROLE.INSTRUCTOR) {
        this.createJitsiVideocall();
      } else {
        window.open(this.state.videocallUrl, '_blank');
      }
    }
  };

  getStyledComponent(isDisabled, isOn) {
    let isOnStyle = {};

    if (this.props.isElectron) {
      isOnStyle = isOn ? { ...styles.videocallIconElectron } : { ...styles.videocallIconOffelectron };
    } else {
      isOnStyle = isOn ? { ...styles.videocallIcon } : { ...styles.videocallIconOff };
    }

    const disabledStyle = isDisabled ? { ...styles.disabledButton } : {};
    return { ...isOnStyle, ...disabledStyle };
  }

  render() {
    const { googleMeetClientId, jitsiDomain } = this.props;
    if (googleMeetClientId && jitsiDomain) {
      return <Fragment />;
    }

    const videocallIcon = (
      <img
        style={this.getStyledComponent(this.state.disabledButton, this.state.isOn)}
        src={`${CONTEXT_PATH}${this.state.iconToShow}`}
        onClick={this.state.disabledButton ? null : this.handleOnClick}
      />
    );

    return (
      <div className="btn-icon margin-right-8" style={{ visibility: this.state.isHidden ? 'hidden' : 'visible' }}>
        {this.state.isOn && videocallIcon}
        {!this.state.isOn && this.props.role === ROLE.INSTRUCTOR && videocallIcon}
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    classroomId: state.classrooms.selectedClassroom.classroom.id,
    classroomName: state.classrooms.selectedClassroom.classroom.name,
    role: state.classrooms.selectedClassroom.classroom.role
  };
}

function mapDispatchToProps(dispatch) {
  return {
    updateOrCreateVideocallInfo: (classroomId, videocallUrl) =>
      dispatch(updateOrCreateVideocallInfo(classroomId, videocallUrl)),
    getClassroomVideocall: classroomId => dispatch(getClassroomVideocall(classroomId))
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(injectSheet(styles)(VideocallTopButton));
